import { Injectable, Injector } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { LocalStorageService } from './localstorage.service';
import { APPConfig } from '../config/env';

const KEY_ID_STORAGE_KEY = 'sign_key_id';
const READY_TIMEOUT_MS = 5000;

@Injectable({ providedIn: 'root' })
export class SigningKeyService {
  private privateKey: CryptoKey | null = null;
  private keyId: string | null = null;
  private readyPromise: Promise<void> = Promise.resolve();

  // Injector 延迟解析 HttpClient，打破
  // JWTInterceptor → SigningKeyService → HttpClient → 拦截器链 的构造期循环依赖
  constructor(
    private injector: Injector,
    private localStorageService: LocalStorageService,
  ) {
    const storedKeyId = this.localStorageService.getStore(KEY_ID_STORAGE_KEY);
    const token = this.localStorageService.getStore('token');
    if (storedKeyId && token) {
      this.startRegistration();
    }
  }

  private get http(): HttpClient {
    return this.injector.get(HttpClient);
  }

  get ready$(): Promise<void> {
    return this.readyPromise;
  }

  getPrivateKey(): CryptoKey | null {
    return this.privateKey;
  }

  getKeyId(): string | null {
    return this.keyId;
  }

  initKeyAfterLogin(): void {
    this.startRegistration();
  }

  async revoke(): Promise<void> {
    const keyIdToRevoke = this.keyId;
    this.privateKey = null;
    this.keyId = null;
    this.localStorageService.removeStore(KEY_ID_STORAGE_KEY);
    if (!keyIdToRevoke) {
      return;
    }
    try {
      const url = this.buildClientKeysUrl() + '/' + keyIdToRevoke;
      await this.http.delete(url, { headers: this.tokenHeader() }).toPromise();
    } catch (e) {
      // ignore; revocation must not block logout
    }
  }

  private startRegistration(): void {
    const registration = this.doRegister();
    this.readyPromise = new Promise<void>(resolve => {
      let settled = false;
      const finish = () => {
        if (settled) { return; }
        settled = true;
        resolve();
      };
      const timer = setTimeout(finish, READY_TIMEOUT_MS);
      registration
        .then(() => { clearTimeout(timer); finish(); })
        .catch(() => { clearTimeout(timer); finish(); });
    });
  }

  private async doRegister(): Promise<void> {
    if (!window.crypto || !window.crypto.subtle) {
      console.warn('[SigningKey] Web Crypto API unavailable, signing disabled');
      return;
    }
    try {
      const keyPair = await window.crypto.subtle.generateKey(
        { name: 'ECDSA', namedCurve: 'P-256' },
        false,
        ['sign', 'verify']
      ) as CryptoKeyPair;

      const publicKeyBytes = await window.crypto.subtle.exportKey('spki', keyPair.publicKey) as ArrayBuffer;
      const publicKeySpkiBase64Url = arrayBufferToBase64Url(publicKeyBytes);

      const response: any = await this.http
        .post(this.buildClientKeysUrl(), {
          publicKeySpkiBase64Url,
          algorithm: 'ECDSA_P256_SHA256_P1363',
          deviceName: (navigator.userAgent || '').substring(0, 200),
          clientGeneratedAt: Date.now()
        }, { headers: this.tokenHeader() })
        .toPromise();

      const newKeyId = response && (response.keyId || (response.DATA && response.DATA.keyId));
      if (!newKeyId) {
        throw new Error('keyId missing in response');
      }
      this.privateKey = keyPair.privateKey;
      this.keyId = newKeyId;
      this.localStorageService.setStore(KEY_ID_STORAGE_KEY, newKeyId);
    } catch (e) {
      console.error('[SigningKey] doRegister failed:', e);
      this.privateKey = null;
      this.keyId = null;
      this.localStorageService.removeStore(KEY_ID_STORAGE_KEY);
    }
  }

  private buildClientKeysUrl(): string {
    // localStorage 里的 API_DOMAIN/API_DIR 理论上应该已经由 app.component.ts 的 initConfig() 写好，
    // 但如果在写入之前就发起了请求（比如扫码自动登录这种一进页面就立刻登录、几乎没有延迟的场景），
    // getStore() 会拿到 null，"null + null" 在 JS 里会被当数字相加算成 0，拼出 "0/security/client-keys"
    // 这种非法的相对路径。这里兜底退回到 env.ts 里模块加载时就同步算好的 APPConfig，避免拼出坏URL。
    const domain = this.localStorageService.getStore('API_DOMAIN') || APPConfig.API_DOMAIN;
    const dir = this.localStorageService.getStore('API_DIR') || APPConfig.API_DIR;
    return domain + dir + '/security/client-keys';
  }

  private tokenHeader(): { [k: string]: string } {
    return { token: this.localStorageService.getStore('token') || '' };
  }
}

function arrayBufferToBase64Url(buffer: ArrayBuffer): string {
  const bytes = new Uint8Array(buffer);
  let binary = '';
  for (let i = 0; i < bytes.length; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}
